import React, { useEffect } from "react";
import { connect } from "react-redux";
import Moment from "moment";
import {
  createStyles,
  IconButton,
  Typography,
  List,
  ListItem,
  Theme,
  WithStyles,
  withStyles,
} from "@material-ui/core";
import { AddCircle, RemoveCircle } from "@material-ui/icons";
import { IndexedGames } from "../../types/game";
import { GlobalState } from "../../types/globalState";
import tournamentActions from "../../actions/tournaments";
import { Game } from "../../types/game";
import { Tournament } from "../../types/tournament";
import { ErrorOnlyCallback } from "../../types/actions";
import Loading from "../Loading";

interface Props extends WithStyles {
  addGameToTournament(tournamentId: string, gameId: string, callback?: ErrorOnlyCallback): void;
  removeGameFromTournament(tournamentId: string, gameId: string, callback?: ErrorOnlyCallback): void;
  getTournamentGames(tournamentId: string, callback?: any): void;
  tournamentId: string;
  tournament?: Tournament;
  editable?: boolean;
  games: Game[];
}

/** Compare Mongo ObjectId vs string reliably (strict === fails across wire formats). */
const idToStr = (id: any): string =>
  id == null ? "" : typeof id === "object" && id.toString ? id.toString() : String(id);

const refId = (t: any): string => {
  if (t == null) return "";
  if (typeof t === "object" && t._id != null) return idToStr(t._id);
  return idToStr(t);
};

const gameIsInTournament = (game: Game, tournamentId: string): boolean => {
  const refs = game.tournaments as any[] | undefined;
  if (!Array.isArray(refs) || !refs.length) return false;
  const tid = idToStr(tournamentId);
  return refs.some(t => refId(t) === tid);
};

/** Same inclusive season check as backend `gameDateWithinTournamentSeason`. */
const gameDateMs = (game: Game): number => {
  const raw: any = game.date;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : NaN;
};

const gameIsInSeason = (game: Game, tournament?: Tournament): boolean => {
  if (!tournament) return false;
  const t = gameDateMs(game);
  const start = new Date(tournament.seasonStart).getTime();
  const end = new Date(tournament.seasonEnd).getTime();
  if (!Number.isFinite(t) || !Number.isFinite(start) || !Number.isFinite(end)) return false;
  return t >= start && t <= end;
};

const byDateDesc = (a: Game, b: Game) => gameDateMs(b) - gameDateMs(a);

const GameRow: React.FC<{
  game: Game;
  classes: Props["classes"];
  action?: React.ReactNode;
}> = ({ game, classes, action }) => (
  <ListItem className={classes.item}>
    <div className={classes.itemBody}>
      <Typography className={classes.itemName} noWrap>
        {game.name}
      </Typography>
      <span className={classes.itemCode}>{game.code}</span>
    </div>
    <Typography className={classes.itemDate} component="span">
      {Moment(game.date).format("ddd DD/MM/YY hh.mm")}
    </Typography>
    {action ? <div className={classes.itemAction}>{action}</div> : null}
  </ListItem>
);

const TournamentGamesManager: React.FC<Props> = props => {
  useEffect(() => {
    props.getTournamentGames(props.tournamentId);
  }, []);

  const games = props.games.reduce(
    (acc: { included: Game[]; remaining: Game[] }, item) => {
      if (gameIsInTournament(item, props.tournamentId)) {
        acc.included.push(item);
      } else if (gameIsInSeason(item, props.tournament)) {
        acc.remaining.push(item);
      }
      return acc;
    },
    { included: [], remaining: [] },
  );
  games.remaining.sort(byDateDesc);

  const { classes } = props;
  const seasonLabel =
    props.tournament &&
    `${Moment(props.tournament.seasonStart).format("ll")} to ${Moment(props.tournament.seasonEnd).format("ll")}`;

  return (
    <div className={classes.root}>
      <Loading style={{ position: "sticky", top: 50 }} />

      {games.included.length ? (
        <div className={classes.section}>
          <Typography className={classes.sectionLabel} component="h2">
            Current games included in this Tournament
          </Typography>
          <List className={classes.sectionList} disablePadding>
            {games.included.map(game => (
              <GameRow
                key={game.code}
                game={game}
                classes={classes}
                action={
                  props.editable ? (
                    <IconButton
                      className={classes.actionBtn}
                      aria-label={`Remove ${game.name} from tournament`}
                      onClick={() =>
                        props.removeGameFromTournament(
                          props.tournamentId,
                          game._id,
                          err => !err && props.getTournamentGames(props.tournamentId),
                        )
                      }
                    >
                      <RemoveCircle color={"secondary"} />
                    </IconButton>
                  ) : null
                }
              />
            ))}
          </List>
        </div>
      ) : null}

      {games.remaining.length ? (
        <div className={classes.section}>
          <Typography className={classes.sectionLabel} component="h2">
            Additional games to select
          </Typography>
          {seasonLabel ? (
            <Typography className={classes.sectionHint} color="textSecondary">
              Games dated {seasonLabel}
            </Typography>
          ) : null}
          <List className={classes.sectionList} disablePadding>
            {games.remaining.map(game => (
              <GameRow
                key={game.code}
                game={game}
                classes={classes}
                action={
                  props.editable ? (
                    <IconButton
                      className={classes.actionBtn}
                      aria-label={`Add ${game.name} to tournament`}
                      onClick={() =>
                        props.addGameToTournament(
                          props.tournamentId,
                          game._id,
                          err => !err && props.getTournamentGames(props.tournamentId),
                        )
                      }
                    >
                      <AddCircle color={"primary"} />
                    </IconButton>
                  ) : null
                }
              />
            ))}
          </List>
        </div>
      ) : null}

      {props.games.length === 0 && (
        <Typography className={classes.empty} color="textSecondary">
          No Game found
        </Typography>
      )}

      {props.editable && props.games.length > 0 && games.remaining.length === 0 ? (
        <Typography className={classes.empty} color="textSecondary">
          No other games fall in this season{seasonLabel ? ` (${seasonLabel})` : ""}.
        </Typography>
      ) : null}
    </div>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      minWidth: 500,
      padding: theme.spacing(0, 1, 2),
      [theme.breakpoints.down("xs")]: {
        minWidth: "100%",
        padding: theme.spacing(0, 0, 2),
      },
    },
    section: {
      "& + &": {
        marginTop: theme.spacing(2.5),
      },
    },
    sectionLabel: {
      fontSize: "0.75rem",
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      padding: theme.spacing(1.5, 0.5, 0.5),
    },
    sectionHint: {
      fontSize: "0.75rem",
      padding: theme.spacing(0, 0.5, 1),
      lineHeight: 1.4,
    },
    sectionList: {
      border: "1px solid rgba(226, 232, 240, 0.95)",
      borderRadius: 8,
      backgroundColor: theme.palette.background.paper,
      overflow: "hidden",
    },
    item: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      paddingTop: theme.spacing(1.25),
      paddingBottom: theme.spacing(1.25),
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(0.75),
      borderBottom: "1px solid rgba(226, 232, 240, 0.95)",
      "&:last-child": {
        borderBottom: "none",
      },
    },
    itemBody: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 4,
    },
    itemName: {
      fontWeight: 600,
      fontSize: "0.9375rem",
      color: theme.palette.text.primary,
      lineHeight: 1.35,
      width: "100%",
    },
    itemCode: {
      display: "inline-flex",
      alignItems: "center",
      height: 22,
      padding: "0 8px",
      border: "1px solid rgba(15, 23, 42, 0.12)",
      borderRadius: 100,
      backgroundColor: "rgba(15, 23, 42, 0.04)",
      color: "rgba(15, 23, 42, 0.82)",
      fontSize: "0.75rem",
      fontWeight: 600,
      letterSpacing: "0.06em",
      lineHeight: 1,
      textTransform: "uppercase",
    },
    itemDate: {
      flexShrink: 0,
      fontSize: "0.75rem",
      color: theme.palette.text.secondary,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
    },
    itemAction: {
      flexShrink: 0,
    },
    actionBtn: {
      padding: 8,
    },
    empty: {
      padding: theme.spacing(2, 1),
      fontSize: "0.875rem",
    },
  });

const sortGame = (games: Game[], tournamentId: string) => {
  return [...games].sort((a, b) => {
    const aIn = gameIsInTournament(a, tournamentId);
    const bIn = gameIsInTournament(b, tournamentId);
    if (aIn === bIn) return 0;
    return aIn ? -1 : 1;
  });
};

const mapStateToProps = (state: GlobalState, props: any) => {
  const { tournamentGames } = state.tournaments;
  let games = tournamentGames.games;
  if (props.editable) {
    const { joinedGames, organisedByMe, history } = state.games;
    let indexedTournamentGames: IndexedGames = {};
    tournamentGames.games &&
      tournamentGames.games.forEach(g => {
        indexedTournamentGames[g._id] = g;
      });
    games = Object.values({ ...history.items, ...organisedByMe, ...joinedGames, ...indexedTournamentGames });
  }
  const listed = (state.games.tournaments || []) as Tournament[];
  const tournament =
    props.tournament || listed.find(t => idToStr(t._id) === idToStr(props.tournamentId));
  return {
    games: sortGame(games, props.tournamentId),
    tournament,
  };
};

export default connect(mapStateToProps, {
  addGameToTournament: tournamentActions.addGameToTournament,
  removeGameFromTournament: tournamentActions.removeGameFromTournament,
  getTournamentGames: tournamentActions.getTournamentGames,
})(withStyles(styles)(TournamentGamesManager));
